"use client";

import { getYandexMetrikaId, isScriptario, publicAppEnv, siteLocale, siteMarket } from "@/lib/config";
import { sendScriptarioAnalyticsEvent } from "@/lib/scriptario-analytics";

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void;
  }
}

const sentEvents = new Set<string>();

function getSessionId() {
  const existing = window.sessionStorage.getItem("analytics_session_id");
  if (existing) return existing;

  const sessionId = Math.random().toString(36).slice(2, 15);
  window.sessionStorage.setItem("analytics_session_id", sessionId);
  return sessionId;
}

function sendYMEvent(eventName: string, params: Record<string, unknown> = {}) {
  const eventData = {
    ...params,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    environment: publicAppEnv,
  };

  if (isScriptario) {
    const scriptarioPayload = {
      ...eventData,
      site_id: siteMarket,
      locale: siteLocale,
    };

    if (!sendScriptarioAnalyticsEvent(eventName, scriptarioPayload)) {
      console.warn("[Analytics] Event dropped: no Scriptario analytics runtime", eventName);
    }
    return;
  }

  const metrikaId = getYandexMetrikaId();
  if (!metrikaId || typeof window === "undefined" || !window.ym) return;

  try {
    window.ym(metrikaId, "reachGoal", eventName, eventData);
  } catch (error) {
    console.error("[YM]", eventName, error);
  }
}

function canSendRegistrationEvent(eventType: string, userEmail: string) {
  const key = `${eventType}_${userEmail}`;
  if (sentEvents.has(key)) return false;
  sentEvents.add(key);
  return true;
}

export function trackClickStartFree(place?: string) {
  sendYMEvent("click_start_free", { place: place || "unknown" });
}

export function trackClickHeaderSignin() {
  sendYMEvent("click_header_signin", { transport_type: "beacon" });
}

export function trackClickTryFreeCenter() {
  sendYMEvent("click_try_free_center");
}

export function trackClickTariffSelect(tariffName?: string) {
  sendYMEvent("click_tariff_select", { tariff_name: tariffName || "" });
}

export function trackLandingFileAttached(durationSeconds?: number, fileType?: string) {
  sendYMEvent("landing_file_attached", {
    duration_seconds: durationSeconds,
    file_type: fileType || "unknown",
  });
}

export function trackClickTranscribeFree() {
  sendYMEvent("click_transcribe_free");
}

export function trackRegistrationSuccess(userEmail: string, source?: string) {
  if (!canSendRegistrationEvent("registration_success", userEmail)) return;
  sendYMEvent("registration_success", {
    user_email: userEmail,
    source: source || "unknown",
  });
}

export function trackGoogleRegistrationSuccess(userEmail: string, source?: string) {
  if (!canSendRegistrationEvent("google_registration_success", userEmail)) return;
  sendYMEvent("google_registration_success", {
    user_email: userEmail,
    source: source || "unknown",
  });
}

export function trackLandingUrlSubmitted(platform?: string) {
  sendYMEvent("landing_url_submitted", { platform: platform || "unknown" });
}

export function trackScrollDepth(depth: number) {
  sendYMEvent("landing_scroll_depth", { depth });
}

const faqOpened = new Set<string>();
export function trackFaqOpen(question: string) {
  if (faqOpened.has(question)) return;
  faqOpened.add(question);
  sendYMEvent("landing_faq_open", { question });
}

export function trackClickPricingToggle(billing: string) {
  sendYMEvent("click_pricing_toggle", { billing });
}

export function trackContactsFormSubmit() {
  sendYMEvent("contacts_form_submit");
}

// Public API landing goals. Deduplicated per page load: one goal per CTA place /
// code tab, so repeated clicks do not inflate conversions in Metrika.
const apiGoalsSent = new Set<string>();

function sendApiGoalOnce(eventName: string, dedupKey: string, params: Record<string, unknown>) {
  const key = `${eventName}:${dedupKey}`;
  if (apiGoalsSent.has(key)) return;
  apiGoalsSent.add(key);
  sendYMEvent(eventName, { ...params, page: window.location.pathname });
}

export function trackApiGetKeyClick(place: string) {
  sendApiGoalOnce("api_get_key_click", place, { place });
}

export function trackApiDocsClick(place: string) {
  sendApiGoalOnce("api_docs_click", place, { place });
}

export function trackApiCodeTabSwitch(tab: string, place: string) {
  sendApiGoalOnce("api_code_tab_switch", `${place}:${tab}`, { tab, place });
}
